import Exception from "../../shared/exception/Exception";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {Parameter} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";

export const PAGERANGE_ALL: string = "*";

export interface PageSelectionInterface {
	selection: Array<number>
}

export default class PageSelection implements PageSelectionInterface, Parameter {
	public selection: Array<number>;

	constructor(data?: any) {
		this.selection = FunctionHelper.exists(data?.selection) ? data.selection : [];
	}

	/**
	 * checks if a page is selected
	 */
	public isPageSelected(page: number): boolean {
		return this.selection.includes(page);
	}

	/**
	 * selects a page adding it to the page selection
	 */
	public selectPage(page: number, pageCount: number): void {
		if (page < 1 || page > pageCount || this.isPageSelected(page)) {
			return;
		}

		this.selection.push(page);
		this.selection.sort((a: number, b: number) => {
			return a > b ? 1 : -1;
		});
	}

	/**
	 * selects pages from a given page number to a given page number
	 */
	public selectPageRange(from: number, to: number, pageCount: number): void {
		for (let pageNumber: number = from; pageNumber <= to; pageNumber++) {
			this.selectPage(pageNumber, pageCount);
		}
	}

	/**
	 * unselects a page removing it from the page selection
	 */
	public unselectPage(page: number): void {
		if (!this.isPageSelected(page)) {
			return;
		}

		this.selection.splice(this.selection.indexOf(page), 1);
	}

	/**
	 * clears the page selection
	 */
	public clearPageSelection(): void {
		this.selection = [];
	}

	/**
	 * select all pages
	 */
	public selectAllPages(pageCount: number): void {
		for (let pageNumber: number = 1; pageNumber <= pageCount; pageNumber++) {
			this.selectPage(pageNumber, pageCount);
		}
	}

	/**
	 * translate page range string to page selection
	 */
	public toString(pageCount: number, currentPage: number): string {
		if (this.selection.length === pageCount) {
			return PAGERANGE_ALL;
		} else if (this.selection.length === 0) {
			return currentPage.toString();
		}

		let selection: Array<string> = [];
		let key: number = 0;

		while (key < this.selection.length) {
			let first = this.selection[key];
			let last = this.selection[key];

			key++;
			while (key < this.selection.length && this.selection[key] === last + 1) {
				last = this.selection[key];
				key++;
			}

			if (last === first) {
				selection.push(last.toString());
			} else if (last === first + 1) {
				selection.push(first + ',' + last);
			} else {
				selection.push(first + '-' + last);
			}
		}

		return selection.join(',');
	}

	/**
	 * translate string to page selection
	 */
	public static fromString(value: string, pageCount: number): PageSelection {
		let pageSelection = new PageSelection();
		let pageRange: string = value.trim();

		if (pageRange === '') {
			throw new Exception('Invalid value.');
		}

		if (pageRange === PAGERANGE_ALL) {
			pageSelection.selectAllPages(pageCount);
			return pageSelection;
		}

		let values: Array<string> = pageRange.split(',');
		for (let parts of values) {
			let temp: string = parts.trim();

			let range: Array<string> = temp.split('-');
			if (range.length > 2) {
				throw new Exception('Invalid value.');
			}

			if (range.length === 1) {
				range[0] = range[0].trim();

				if (range[0] === PAGERANGE_ALL) {
					pageSelection.selectAllPages(pageCount);
					return pageSelection;
				}

				let pageNumber: number = parseInt(range[0]);

				if (isNaN(pageNumber) || pageNumber === 0 || pageNumber > pageCount) {
					throw new Exception('Invalid value.');
				}

				pageSelection.selectPage(pageNumber, pageCount);
				continue;
			}

			range[0] = range[0].trim();
			range[1] = range[1].trim();

			let first: number = parseInt(range[0]);
			if (isNaN(first) || first > pageCount) {
				throw new Exception('Invalid value.');
			}

			if (range[1] === PAGERANGE_ALL) {
				pageSelection.selectPageRange(first, pageCount, pageCount);
				continue;
			}

			let last: number = parseInt(range[1]);
			if (isNaN(last) || last > pageCount) {
				throw new Exception('Invalid value.');
			}

			if (first > last) {
				let cache: number = first;
				first = last;
				last = cache;
			}

			pageSelection.selectPageRange(first, last, pageCount);
		}

		return pageSelection;
	}

	public static fromJson(data: any): PageSelection {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new PageSelection(data);
	}

	public toJson(): any {
		return {
			'selection': this.selection
		};
	}

	public clone(): PageSelection {
		return PageSelection.fromJson(this.toJson());
	}
}